/**
 * Admin Deactivate User Modal Component
 *
 * Confirmation modal for deactivating a user.
 *
 * @fileoverview Admin user deactivation confirmation modal
 */

import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  VStack,
} from '@chakra-ui/react';
import type { AdminUser } from '../../types/admin';

interface AdminDeactivateUserModalProps {
  /** Modal open state */
  isOpen: boolean;
  /** Close callback */
  onClose: () => void;
  /** User to deactivate */
  user: AdminUser | null;
  /** Confirm callback */
  onConfirm: () => Promise<boolean>;
  /** Loading state */
  loading: boolean;
}

/**
 * Admin Deactivate User Modal Component
 *
 * Confirmation modal for deactivating a user.
 *
 * @param props - Component props
 * @returns React component
 *
 * @example
 * <AdminDeactivateUserModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   user={selectedUser}
 *   onConfirm={handleDeactivate}
 *   loading={loading}
 * />
 */
export const AdminDeactivateUserModal: React.FC<AdminDeactivateUserModalProps> = ({
  isOpen,
  onClose,
  user,
  onConfirm,
  loading,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');

  /**
   * Handle confirmation
   */
  const handleConfirm = async () => {
    const success = await onConfirm();
    if (success) {
      onClose();
    }
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader>Deactivate User</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              Deactivating a user will prevent them from logging in.
            </Alert>

            <Text>
              Are you sure you want to deactivate{' '}
              <Text as="span" fontWeight="bold">
                {user.email}
              </Text>
              ?
            </Text>

            <Text fontSize="sm" color="gray.500">
              This action can be reversed by activating the user again.
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="orange"
            onClick={handleConfirm}
            isLoading={loading}
          >
            Deactivate
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdminDeactivateUserModal;
